/* @flow */
import { Failure, Success } from './index'

import type { ValueSuccessType, ValidationType } from './index'
import type { TranslationType } from '../helpers/i18n'
import type { ValidatorReturnType } from './validators'
export type ValidationListType = Array<(string) => ValidationType>

/**
 * Validate a function with a predicate that returns either `true` or `false`.
 * @param {function} pred - The test predicate.
 * @return {function} A validation function.
 * @example
 * predicate(v => v == 4)(4) == new Success(4)
 * @example
 * predicate(v => v == 4)(5) ==
 *   new Failure(["'5' doesn't pass the predicate."])
 */
const predicate = (predicateFn: (ValueSuccessType) => boolean) => (value: ValueSuccessType): ValidationType => {
  if (predicateFn(value)) {
    return new Success(value)
  } else {
    return new Failure('doesnt satisfy the predicate.')
  }
}

/**
 * Attach a static error message to a validation function.
 * @param {function} validation - The validation you want to attach a new error message to
 * @param {*} message - The validation message
 * @return {function} The original validator, but with a different error message if it fails.
 * @example
 * withMessage(regexp(/[ABC]/)("A"), "Not 'A'")("B") == new Failure(["Not 'A'"])
 */
const withMessage = (validation: (string) => ValidationType, message: TranslationType) => (
  value: string
): ValidationType => {
  return validation(value).orElse(() => new Failure(message))
}

/**
 * Attach an error message to a validation function.
 * @param {function()} validation - The validation you want to attach a new error message to
 * @param {function()} msgFn - Function to generate an error from the value.
 * @return {function()} The original validator, but with a different error message if it fails.
 * @example
 * withMessageFn(regexp(/[ABC]/)("A"), v => v + " is not 'A'")("B") == new Failure(["B is not 'A'"])
 */
const withMessageFn = (validation: (string) => ValidationType, msgFn: (any) => mixed) => (
  value: string
): ValidationType => {
  return validation(value).orElse(() => new Failure(msgFn(value)))
}

/**
 * Combine two validation function with a short-circuiting `or`.
 * The validator will fail with the last error.
 * @param {function} a - The first validation to try.
 * @param {function} b - The second validation to try.
 * @return {function} The new validator.
 * @example
 * or(number, predicate(v => v == "A"))(4) == new Success(4)
 * @example
 * or(number, predicate(v => v == "A"))("A") == new Success("A")
 * @example
 * or(number, predicate(v => v == "A"))("B") ==
 *   new Failure(["'B' doesn't pass the predicate."])
 */
const or = (...args: ValidationListType): ValidatorReturnType => {
  return ors(args)
}

const ors = (validators: ValidationListType): ValidatorReturnType => {
  const fn: ValidatorReturnType = (value: string) => {
    let res = new Failure('array is empty')
    for (let i = 0; i < validators.length; i++) {
      res = validators[i](value)
      if (res instanceof Success) {
        return res
      }
    }
    return res
  }
  // $FlowFixMe
  fn.arrayCheck = validators.some((v) => v.arrayCheck)
  return fn
}

/**
 * Combine two validation function with a short-circuiting `and`.
 * The validator will fail with the first error.
 * @param {function} a - The first validation to test.
 * @param {function} b - The second validation to test.
 * @return {function} The new validator.
 * @example
 * and(nonEmptyString(), email)("foo@bar.com") == new Success("foo@bar.com")
 * @example
 * and(nonEmptyString(), email)("I'm not empty") ==
 *   new Failure(['"I\'m not empty" is not a valid E-Mail']);
 * @example
 * and(nonEmptyString(), email)("") == new Failure(["Should be a non-empty string"]);
 */
const and = (...args: ValidationListType): ValidatorReturnType => {
  return ands(args)
}

const ands = (validators: ValidationListType): ValidatorReturnType => {
  const fn: ValidatorReturnType = (value: string) => {
    let res = new Failure('array is empty')
    for (var i = 0; i < validators.length; i++) {
      res = validators[i](value)
      if (res instanceof Failure) {
        return res
      }
    }
    return res
  }
  // $FlowFixMe
  fn.arrayCheck = validators.some((v) => v.arrayCheck)
  return fn
}

export { predicate, withMessage, withMessageFn, or, and, ors, ands }
